// tickets/ai.md AI-302 — block tool-call protocol text from the bubble, at
// the workspace STATE layer: the transport's final-text patch (live turns)
// and sessions._toChatMessages (every history reload) both pass assistant
// text through this ONE gate. Since server b8fa8af1 (AI-203) recovers
// whole-text leaks against the task catalog server-side, the primary case
// left here is reload-from-history of PRE-fix conversations (persisted
// leaked answers), plus defense in depth for provider paths that bypass the
// fixed loop.
//
// Deliberately conservative — an assistant legitimately answers with JSON
// and code all day:
//   - fenced code blocks are NEVER flagged (a fence is a formatted answer)
//   - ordinary prose that merely CONTAINS JSON is never flagged (whole-text
//     shapes only)
//   - the measured artifact is a TRUNCATED fragment ('"content": "hello C"
//     } }'), which no JSON.parse catches — the fragment rule requires a
//     leading quoted JSON key, trailing brace residue, no sentence
//     structure, and a short length, so a real answer never matches.

export const PROTOCOL_LEAK_NOTICE =
  'This reply contained internal tool instructions instead of an answer — ' +
  'the raw output was kept for diagnosis. Ask again if anything is missing.'

// → { leaked: false } | { leaked: true, text: NEUTRAL, raw }
export const sanitizeAssistantProtocolText = (text) => {
  const t = String(text || '').trim()
  if (!t) return { leaked: false }
  if (t.startsWith('```')) return { leaked: false }

  // Whole-text JSON that parses to a call shape: {tool_call:{name,…}} or
  // a bare {name, arguments|args}.
  if (t.startsWith('{') && t.endsWith('}')) {
    try {
      const p = JSON.parse(t)
      if (p && typeof p === 'object' && !Array.isArray(p)) {
        const bag = p.tool_call && typeof p.tool_call === 'object' ? p.tool_call : p
        const isCall =
          typeof bag.name === 'string' &&
          bag.name.trim() &&
          (p.tool_call !== undefined ||
            Object.prototype.hasOwnProperty.call(bag, 'arguments') ||
            Object.prototype.hasOwnProperty.call(bag, 'args'))
        if (isCall) return { leaked: true, text: PROTOCOL_LEAK_NOTICE, raw: t }
      }
    } catch (_) {
      /* unparseable — fall through to the fragment rules */
    }
    // A truncated {"tool_call": … prefix that no longer parses.
    if (/^\{\s*"tool_call"\s*:/.test(t)) return { leaked: true, text: PROTOCOL_LEAK_NOTICE, raw: t }
  }

  // The wire protocol markers as the message body.
  if (/^\[Tool call\]/.test(t) || /^RTLU to=functions\./.test(t)) {
    return { leaked: true, text: PROTOCOL_LEAK_NOTICE, raw: t }
  }

  // The measured artifact: a mid-JSON fragment — leading quoted key, trailing
  // brace residue, short, and no prose sentence anywhere in it.
  const fragment =
    /^"[A-Za-z_][\w-]*"\s*:/.test(t) &&
    /[}\]]\s*$/.test(t) &&
    t.length <= 400 &&
    !/[.!?]\s+[A-Z]/.test(t)
  if (fragment) return { leaked: true, text: PROTOCOL_LEAK_NOTICE, raw: t }

  return { leaked: false }
}
